import React, { useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Form, Grid } from "antd";
import panels from "@/constants/panels";
import { showPanel } from "@/pages/shared/shared.reducer";
import {
  getDetailsByUserId,
  clearContactDetails,
} from "@/common/user-profile/user-profile-reducer";
import { haveAbilityToDeleteChangeComment } from "@/pages/login/permission-service";
import WithAvatar from "@/common/with-avatar";
import FloatInput from "@/common/float-input";
import { useFormContext } from "@/common/form-context";
import { useUnderChangeCommentMutation } from "@/pages/dashboard/dashboard.reducer";

const DocumentCommentPanel = ({ permissions, refetch }) => {
  const dispatch = useDispatch();
  const [underChangeComment] = useUnderChangeCommentMutation();
  const formRef = useRef(null);
  const document = useSelector(
    (state) => state.dashboardPage.showChangeCommentPanel?.item
  );
  const { name, id } = document.underChangeUser;
  const screens = Grid.useBreakpoint();
  const isMobile = screens.xs;

  useEffect(() => {
    formRef.current?.setFieldsValue({ comment: document.underChangeComment });
  }, [document.underChangeComment]);

  const { form, onFinish, markFieldAsTouched, isAnyFieldTouched } =
    useFormContext();

  // Removed unused exitPanel function

  const showProfilePanel = (item) => {
    dispatch(clearContactDetails());
    item && dispatch(getDetailsByUserId(item));
    dispatch(showPanel(panels.siteUser));
  };

  const handleDeleteComment = useCallback(() => {
    underChangeComment({
      comment: null,
      documents: [document],
      panelId: "showChangeCommentPanel",
      refetch,
    });
  }, [document, underChangeComment, refetch]);

  const handleSubmit = useCallback(
    (value) => {
      const { comment } = value;
      underChangeComment({
        comment,
        documents: [document],
        panelId: "showChangeCommentPanel",
        refetch,
      });
    },
    [document, underChangeComment, refetch]
  );

  const checkApprovalOrCheck = (comment) => {
    const uploadedRevisionPattern = /Uploaded Revision/i;

    const approvalOrCheckPattern = /(For Approval|For Check)/i;

    const test =
      uploadedRevisionPattern.test(comment) &&
      approvalOrCheckPattern.test(comment);

    return test;
  };

  const canDeleteComment = haveAbilityToDeleteChangeComment(
    permissions,
    document
  );

  return (
    <>
      <Form form={form} ref={formRef} onFinish={onFinish(handleSubmit)}>
        <div style={{ marginBottom: 14 }}>
          <div className="changedBy display-center table-action-link">
            <WithAvatar
              className={document.underChangeUser?.active ? "action-link" : "text-red"}
              name={name}
              handleClick={(e) => {
                e.stopPropagation();
                showProfilePanel(id);
              }}
            />
          </div>
        </div>
        {canDeleteComment ? (
          <Form.Item
            name="comment"
            //initialValue={comment}
            rules={[
              {
                required: true,
                message:
                  "Documents cannot be placed under change without a comment. If you would like to remove the comment, click Delete Comment below",
              },
              {
                whitespace: true,
                message:
                  "Documents cannot be placed under change without a comment. If you would like to remove the comment, click Delete Comment below",
              },
            ]}
          >
            <FloatInput
              multiline
              label="Comment"
              id="comment"
              showCount
              maxLength={1000}
              onChange={(e) => {
                const value = e.target.value;
                markFieldAsTouched(e.target.id);
                formRef.current?.setFieldsValue({ comment: value });
              }}
              autoSize={{ minRows: 3 }}
              disabled={
                !canDeleteComment ||
                checkApprovalOrCheck(document.underChangeComment)
              }
            />
          </Form.Item>
        ) : (
          <div className="comment-block">
            <div style={{ marginBottom: 2, color: "var(--dms-color-text-muted)" }}>Comment</div>
            <div>{document.underChangeComment}</div>
          </div>
        )}
        {/* <div className='comment-block'>
                    <div style={{ marginBottom: 2, color: "var(--dms-color-text-muted)" }}>
                        Comment
                    </div>
                    <div>
                        {document.underChangeComment}
                    </div>
                </div> */}

        <div className="body-large-roman mt-3" style={{ color: "var(--dms-color-text-muted)" }}>
          Document / drawing #:
        </div>
        <div style={{ gap: 6, marginBottom: 26 }}>
          <span className="body-large-roman" style={{ color: "var(--dms-color-text-muted)" }}>
            ●&nbsp;
          </span>
          <span style={{ color: "var(--dms-color-text)" }}>{document.documentNumber}</span>
        </div>
      </Form>
      <div className="footer document-detail-footer" 
        style={ isMobile ? {
          display: "grid",
          rowGap: "10px",
          width: "100%",
          minHeight: "22vh",
          alignContent: "flex-start"
        } : {
            display: "flex",
            paddingRight: "45px"
        }}>
        {canDeleteComment && (
          <Button
            onClick={() => form.submit()}
            type="primary"
            disabled={
              !isAnyFieldTouched() ||
              !canDeleteComment ||
              checkApprovalOrCheck(document.underChangeComment)
            }
            className="display-center me-2"
          >
            <i className="ri-edit-2-line" />
            Save Changes
          </Button>
        )}
        {canDeleteComment && (
          <Button
            onClick={handleDeleteComment}
            disabled={
              !canDeleteComment ||
              checkApprovalOrCheck(document.underChangeComment)
            }
            variant="solid" color="danger"
          >
            <i className="ri-delete-bin-line" />
            Delete Comment
          </Button>
        )}
      </div>
    </>
  );
};
export default DocumentCommentPanel;
